import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Product, Customer, Order } from "../../types";
import { POSPayment } from "../../pages/pos/types";
import { productService } from "../../services/productService";
import { customerService } from "../../services/customerService";
import { orderService } from "../../services/orderService";
import { orderChannelService } from "../../services/orderChannelService";
import { posCache } from "../../utils/posCache";
import { samePrice, unitPriceFor } from "../../utils/productPricing";

// Cart Item Interface
export interface CartItem {
    product: Product;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    // Fixed at add-time, same as OrderForm's gift rows — price is always 0
    // and there's no later toggle.
    isGift: boolean;
    // Staff typed this line's price (or took it from the customer's
    // history): quantity changes keep it instead of following combo prices.
    priceSetByHand?: boolean;
}

// A line's price for its quantity — today's price, or the combo price once
// the quantity reaches one — unless it's a gift or staff set it by hand.
function repriceLine(item: CartItem) {
    if (!item.isGift && !item.priceSetByHand) {
        item.unitPrice = unitPriceFor(item.product, item.quantity);
    }
    item.totalPrice = Math.round(item.quantity * item.unitPrice * 100) / 100;
}

// POS State Interface
export interface POSState {
    // Data
    products: Product[];
    customers: Customer[];
    cart: CartItem[];
    selectedCustomer: Customer | null;

    // UI State
    productSearch: string;
    loading: boolean;
    error: string;
    success: string;

    // Transaction State
    deliveryCharge: number;
    discount: number;
    lastCompletedOrder: Order | null;
    lastPaymentMethod: string;
    lastAmountPaid: number;

    // Dialog States
    paymentDialogOpen: boolean;
    quantityDialogOpen: boolean;
    quickProductDialogOpen: boolean;
    receiptModalOpen: boolean;
    // After a sale (created PENDING) the receipt is shown first, so the
    // customer is served before anything else; closing that receipt then
    // asks whether to mark the order DELIVERED — see confirmMarkDelivered.
    // `deliveryConfirmPending` remembers that the open receipt belongs to a
    // fresh sale (as opposed to "View Last Receipt"), so only that close
    // leads to the question.
    deliveryConfirmPending: boolean;
    deliveryConfirmOpen: boolean;
    selectedProductForQuantity: Product | null;
    quantityValue: number;
    quantityDialogIsGift: boolean;

    // Receipt State
    receiptCustomer: Customer | null;
    receiptOrder: Order | null;
    // Persisted receipt state for "View Last Receipt" — survives cart clear + modal close
    lastReceiptCustomer: Customer | null;

    // Persistence flag
    isInitialized: boolean;

    // Cache / offline state
    lastSyncAt: number | null;
    usingCachedData: boolean;
    syncing: boolean;

    // The "POS" order channel's id, resolved once at load time so every
    // order created here is tagged as offline/POS without a per-checkout
    // network round-trip. Null if unresolved (offline, or channel deleted) —
    // order creation still proceeds, it's just left untagged.
    posOrderChannelId: string | null;
}

// Initial State
const initialState: POSState = {
    // Data
    products: [],
    customers: [],
    cart: [],
    selectedCustomer: null,

    // UI State
    productSearch: "",
    loading: false,
    error: "",
    success: "",

    // Transaction State
    deliveryCharge: 0,
    discount: 0,
    lastCompletedOrder: null,
    lastPaymentMethod: "",
    lastAmountPaid: 0,

    // Dialog States
    paymentDialogOpen: false,
    quantityDialogOpen: false,
    quickProductDialogOpen: false,
    receiptModalOpen: false,
    deliveryConfirmPending: false,
    deliveryConfirmOpen: false,
    selectedProductForQuantity: null,
    quantityValue: 1,
    quantityDialogIsGift: false,

    // Receipt State
    receiptCustomer: null,
    receiptOrder: null,
    lastReceiptCustomer: null,

    // Persistence flag
    isInitialized: false,

    // Cache / offline state
    lastSyncAt: null,
    usingCachedData: false,
    syncing: false,

    posOrderChannelId: null,
};

// Fetch every product from the API by paginating — no hard cap
async function fetchAllProducts(): Promise<Product[]> {
    const PAGE_SIZE = 500;
    const first = await productService.getProducts({ skip: 0, limit: PAGE_SIZE });
    const total = first.total;
    const all: Product[] = [...(first.data || first.items || [])];

    if (all.length >= total) return all;

    const remaining = Math.ceil((total - all.length) / PAGE_SIZE);
    const pages = await Promise.all(
        Array.from({ length: remaining }, (_, i) =>
            productService.getProducts({ skip: (i + 1) * PAGE_SIZE, limit: PAGE_SIZE }),
        ),
    );
    for (const page of pages) {
        all.push(...(page.data || page.items || []));
    }
    return all;
}

// Fetch every customer from the API by paginating — no hard cap
async function fetchAllCustomers(): Promise<Customer[]> {
    const PAGE_SIZE = 500;
    const first = await customerService.getCustomers({ skip: 0, limit: PAGE_SIZE });
    const total = first.total;
    const all: Customer[] = [...first.customers];

    if (all.length >= total) return all;

    const remaining = Math.ceil((total - all.length) / PAGE_SIZE);
    const pages = await Promise.all(
        Array.from({ length: remaining }, (_, i) =>
            customerService.getCustomers({ skip: (i + 1) * PAGE_SIZE, limit: PAGE_SIZE }),
        ),
    );
    for (const page of pages) {
        all.push(...page.customers);
    }
    return all;
}

// Resolve the "POS" order channel's id. Never throws — a lookup failure
// (offline, channel renamed/deleted) just means orders go untagged, it
// must never block product/customer loading or order creation.
async function fetchPOSOrderChannelId(): Promise<string | null> {
    try {
        const channels = await orderChannelService.getOrderChannelsSimple(true);
        return channels.find((c) => c.name === "POS")?.order_channel_id ?? null;
    } catch {
        return null;
    }
}

// Async Thunks
export const loadPOSData = createAsyncThunk(
    "pos/loadData",
    async (_, { rejectWithValue }) => {
        // 1. Try loading from IndexedDB cache first
        let cachedProducts: Product[] | null = null;
        let cachedCustomers: Customer[] | null = null;
        let cachedLastSync: number | null = null;
        try {
            [cachedProducts, cachedCustomers, cachedLastSync] = await Promise.all([
                posCache.getProducts(),
                posCache.getCustomers(),
                posCache.getLastSync(),
            ]);
        } catch {
            // IndexedDB unavailable (private browsing, etc.) — continue without cache
        }

        // 2. Attempt fresh API fetch
        try {
            const [products, customers, posOrderChannelId] = await Promise.all([
                fetchAllProducts(),
                fetchAllCustomers(),
                fetchPOSOrderChannelId(),
            ]);
            const lastSyncAt = Date.now();

            try {
                await posCache.saveAll(products, customers, lastSyncAt);
            } catch {
                // Cache write failure is non-critical
            }

            return { products, customers, lastSyncAt, fromCache: false, posOrderChannelId };
        } catch (apiError: any) {
            // API failed — fall back to cache if available
            if (cachedProducts) {
                return {
                    products: cachedProducts,
                    customers: cachedCustomers ?? [],
                    lastSyncAt: cachedLastSync,
                    fromCache: true,
                };
            }
            return rejectWithValue(
                apiError.response?.data?.detail || "Failed to load POS data. No cached data available.",
            );
        }
    },
);

export const refreshProducts = createAsyncThunk(
    "pos/refreshProducts",
    async (_, { rejectWithValue }) => {
        try {
            const [products, customers] = await Promise.all([
                fetchAllProducts(),
                fetchAllCustomers(),
            ]);
            const lastSyncAt = Date.now();
            try {
                await posCache.saveAll(products, customers, lastSyncAt);
            } catch {
                // non-critical
            }
            return { products, customers, lastSyncAt };
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.detail || "Failed to sync POS data",
            );
        }
    },
);

export const processOrder = createAsyncThunk(
    "pos/processOrder",
    async (
        orderData: {
            payments: POSPayment[];
            cart: CartItem[];
            selectedCustomer: Customer | null;
            deliveryCharge: number;
            discount: number;
            allowNegativeStock?: boolean;
        },
        { rejectWithValue, dispatch, getState },
    ) => {
        try {
            const { pos } = getState() as { pos: POSState };
            const {
                payments,
                cart,
                selectedCustomer,
                deliveryCharge,
                discount,
                allowNegativeStock = false,
            } = orderData;

            const subtotal = cart.reduce(
                (sum, item) => sum + item.totalPrice,
                0,
            );
            const finalTotal = Math.max(
                subtotal + deliveryCharge - discount,
                0,
            );

            // Get default addresses
            const defaultShippingAddress = selectedCustomer?.addresses?.find(
                (addr) => addr.is_default && addr.address_type === "SHIPPING",
            );
            // Sales created here start PENDING (stock is reserved, not
            // fulfilled) — the cashier confirms delivery afterward via
            // confirmMarkDelivered, which is what actually reduces stock.
            const orderPayload = {
                customer_id: selectedCustomer?.customer_id,
                status: "PENDING" as const,
                total_amount: finalTotal,
                discount_amount: discount,
                shipping_amount: deliveryCharge,
                shipping_address: defaultShippingAddress
                    ? `${defaultShippingAddress.street_address}, ${defaultShippingAddress.city}`
                    : "In-store pickup",
                order_channel_id: pos.posOrderChannelId || undefined,
                // Always true — harmless when the cart has no gift items,
                // and a cashier who marks something as a gift expects the
                // customer's receipt to actually show it (see ReceiptModal,
                // which only hides gift lines when this is false).
                show_gifts_on_bill: true,
                items: cart.map((item: CartItem) => ({
                    product_id: item.product.product_id,
                    quantity: item.quantity,
                    unit_price: item.isGift ? 0 : item.unitPrice,
                    total_price: item.isGift ? 0 : item.totalPrice,
                    is_gift: item.isGift,
                })),
                // Passed through as-is (not rebuilt field-by-field) so
                // POSPayment and CreateOrderRequest.payments[] can never
                // drift apart silently — see git history: an explicit
                // field list here previously dropped account_id, so a
                // payment tagged to an account in the POS dialog was
                // silently saved untagged.
                payments: payments.filter((p) => p.amount > 0),
                allow_negative_stock: allowNegativeStock,
            };

            const result = await orderService.createOrder(orderPayload);

            // Get detailed order for receipt
            const detailedOrder = await orderService.getOrderForReceipt(
                result.order_id,
            );

            // Send automatic Telegram notification if this order was somehow
            // created above PENDING — a no-op for the normal PENDING case.
            // The DELIVERED transition (and its notification) happens later,
            // in confirmMarkDelivered, once the cashier confirms delivery.
            try {
                const { telegramService } =
                    await import("../../services/telegramService");
                await telegramService.sendOrderNotificationIfNeeded(
                    detailedOrder,
                );
            } catch (telegramError) {
                console.error(
                    "Failed to send automatic Telegram notification:",
                    telegramError,
                );
                // Don't throw - automatic notification failure shouldn't block order creation
            }

            // Refresh products to get updated stock
            dispatch(refreshProducts());

            const amountPaid = orderPayload.payments.reduce(
                (sum, p) => sum + p.amount,
                0,
            );
            const paymentMethod =
                orderPayload.payments.length === 0
                    ? ""
                    : orderPayload.payments.length === 1
                      ? orderPayload.payments[0].payment_method
                      : "Multiple";

            return {
                order: result,
                detailedOrder,
                paymentMethod,
                amountPaid,
                customer: selectedCustomer,
            };
        } catch (error: any) {
            const detail = error.response?.data?.detail;
            const message =
                typeof detail === "string"
                    ? detail
                    : Array.isArray(detail) && detail.length > 0
                      ? detail.map((e: any) => e.msg || String(e)).join("; ")
                      : error.message || "Failed to process order";
            return rejectWithValue(message);
        }
    },
);

export const confirmMarkDelivered = createAsyncThunk(
    "pos/confirmMarkDelivered",
    async (orderId: string, { rejectWithValue, dispatch }) => {
        try {
            await orderService.updateOrderStatus(orderId, "DELIVERED");
            const freshOrder = await orderService.getOrderForReceipt(orderId);

            // Mirrors OrderStatusManager.handleStatusUpdate's notification
            // trigger for a PENDING -> DELIVERED transition.
            try {
                const { telegramService } =
                    await import("../../services/telegramService");
                await telegramService.sendOrderNotificationIfNeeded(
                    freshOrder,
                    "PENDING",
                );
            } catch (telegramError) {
                console.error(
                    "Failed to send automatic Telegram notification:",
                    telegramError,
                );
            }

            // Reflect the now-reduced stock.
            dispatch(refreshProducts());

            return freshOrder;
        } catch (error: any) {
            const detail = error.response?.data?.detail;
            const message =
                typeof detail === "string"
                    ? detail
                    : error.message || "Failed to mark order as delivered";
            return rejectWithValue(message);
        }
    },
);

// POS Slice
const posSlice = createSlice({
    name: "pos",
    initialState,
    reducers: {
        // UI Actions
        setProductSearch: (state, action: PayloadAction<string>) => {
            state.productSearch = action.payload;
        },

        clearMessages: (state) => {
            state.error = "";
            state.success = "";
        },

        setError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.success = "";
        },

        setSuccess: (state, action: PayloadAction<string>) => {
            state.success = action.payload;
            state.error = "";
        },

        // Cart Actions
        addToCart: (
            state,
            action: PayloadAction<{
                product: Product;
                quantity: number;
                isGift?: boolean;
            }>,
        ) => {
            const { product, quantity, isGift = false } = action.payload;
            const existingItem = state.cart.find(
                (item) => item.product.product_id === product.product_id,
            );

            if (existingItem) {
                // Gift status (like a hand-set price) is fixed by whatever's
                // already in the cart — re-adding the same product only bumps
                // quantity (and the combo price follows it).
                existingItem.quantity += quantity;
                repriceLine(existingItem);
            } else {
                const newItem: CartItem = {
                    product,
                    quantity,
                    unitPrice: 0,
                    totalPrice: 0,
                    isGift,
                };
                repriceLine(newItem);
                state.cart.push(newItem);
            }
        },

        updateCartQuantity: (
            state,
            action: PayloadAction<{ productId: string; quantity: number }>,
        ) => {
            const { productId, quantity } = action.payload;
            const item = state.cart.find(
                (item) => item.product.product_id === productId,
            );

            if (item) {
                if (quantity <= 0) {
                    state.cart = state.cart.filter(
                        (item) => item.product.product_id !== productId,
                    );
                } else {
                    item.quantity = quantity;
                    repriceLine(item);
                }
            }
        },

        updateCartItemPrice: (
            state,
            action: PayloadAction<{ productId: string; unitPrice: number }>,
        ) => {
            const { productId, unitPrice } = action.payload;
            const item = state.cart.find(
                (item) => item.product.product_id === productId,
            );
            if (item && unitPrice >= 0) {
                item.unitPrice = unitPrice;
                // Setting it back to the automatic price (e.g. "Use it" on the
                // combo hint) lets it follow quantity changes again.
                item.priceSetByHand = !samePrice(unitPrice, unitPriceFor(item.product, item.quantity));
                repriceLine(item);
            }
        },

        removeFromCart: (state, action: PayloadAction<string>) => {
            state.cart = state.cart.filter(
                (item) => item.product.product_id !== action.payload,
            );
        },

        clearCart: (state) => {
            state.cart = [];
            state.selectedCustomer = null;
            state.deliveryCharge = 0;
            state.discount = 0;
        },

        // Customer Actions
        setSelectedCustomer: (
            state,
            action: PayloadAction<Customer | null>,
        ) => {
            state.selectedCustomer = action.payload;
        },

        addCustomer: (state, action: PayloadAction<Customer>) => {
            state.customers.push(action.payload);
        },

        // Transaction Actions
        setDeliveryCharge: (state, action: PayloadAction<number>) => {
            state.deliveryCharge = action.payload;
        },

        setDiscount: (state, action: PayloadAction<number>) => {
            state.discount = action.payload;
        },

        // Dialog Actions
        openPaymentDialog: (state) => {
            state.paymentDialogOpen = true;
        },

        closePaymentDialog: (state) => {
            state.paymentDialogOpen = false;
        },

        openQuantityDialog: (
            state,
            action: PayloadAction<{
                product: Product;
                quantity?: number;
                isGift?: boolean;
            }>,
        ) => {
            state.quantityDialogOpen = true;
            state.selectedProductForQuantity = action.payload.product;
            state.quantityValue = action.payload.quantity || 1;
            state.quantityDialogIsGift = action.payload.isGift || false;
        },

        closeQuantityDialog: (state) => {
            state.quantityDialogOpen = false;
            state.selectedProductForQuantity = null;
            state.quantityValue = 1;
            state.quantityDialogIsGift = false;
        },

        setQuantityValue: (state, action: PayloadAction<number>) => {
            state.quantityValue = action.payload;
        },

        setQuantityDialogIsGift: (state, action: PayloadAction<boolean>) => {
            state.quantityDialogIsGift = action.payload;
        },

        openQuickProductDialog: (state) => {
            state.quickProductDialogOpen = true;
        },

        closeQuickProductDialog: (state) => {
            state.quickProductDialogOpen = false;
        },

        openReceiptModal: (
            state,
            action: PayloadAction<{
                customer: Customer | null;
                order: Order | null;
            }>,
        ) => {
            state.receiptModalOpen = true;
            state.receiptCustomer = action.payload.customer;
            state.receiptOrder = action.payload.order;
        },

        closeReceiptModal: (state) => {
            state.receiptModalOpen = false;
            if (state.deliveryConfirmPending) {
                // Receipt of a fresh sale dismissed — now ask about delivery.
                // receiptOrder stays set: the question is about that order.
                state.deliveryConfirmPending = false;
                state.deliveryConfirmOpen = true;
                return;
            }
            state.receiptCustomer = null;
            state.receiptOrder = null;
        },

        // Declining the post-sale "mark as delivered?" prompt — the order
        // stays PENDING (stock stays reserved). The receipt was already
        // shown, so the sale flow ends here.
        skipDeliveryConfirm: (state) => {
            state.deliveryConfirmOpen = false;
            state.receiptCustomer = null;
            state.receiptOrder = null;
        },

        // Product Actions
        addProduct: (state, action: PayloadAction<Product>) => {
            state.products.push(action.payload);
        },

        // Persistence Actions
        initializeFromStorage: (
            state,
            action: PayloadAction<Partial<POSState>>,
        ) => {
            const savedState = action.payload;

            // Only restore specific fields, not all state
            if (savedState.cart) state.cart = savedState.cart;
            if (savedState.selectedCustomer)
                state.selectedCustomer = savedState.selectedCustomer;
            if (savedState.deliveryCharge !== undefined)
                state.deliveryCharge = savedState.deliveryCharge;
            if (savedState.discount !== undefined)
                state.discount = savedState.discount;
            if (savedState.productSearch)
                state.productSearch = savedState.productSearch;
            if (savedState.lastCompletedOrder)
                state.lastCompletedOrder = savedState.lastCompletedOrder;
            if (savedState.lastPaymentMethod)
                state.lastPaymentMethod = savedState.lastPaymentMethod;
            if (savedState.lastAmountPaid !== undefined)
                state.lastAmountPaid = savedState.lastAmountPaid;
            if (savedState.lastReceiptCustomer !== undefined)
                state.lastReceiptCustomer = savedState.lastReceiptCustomer;

            state.isInitialized = true;
        },

        resetPOSState: (state) => {
            return { ...initialState, isInitialized: true };
        },
    },
    extraReducers: (builder) => {
        // Load POS Data
        builder
            .addCase(loadPOSData.pending, (state) => {
                state.loading = true;
                state.error = "";
            })
            .addCase(loadPOSData.fulfilled, (state, action) => {
                state.loading = false;
                state.products = action.payload.products;
                state.customers = action.payload.customers;
                state.lastSyncAt = action.payload.lastSyncAt;
                state.usingCachedData = action.payload.fromCache;
                // Cache-fallback loads don't re-resolve this — keep whatever
                // was already resolved rather than wiping it to null.
                state.posOrderChannelId =
                    action.payload.posOrderChannelId ?? state.posOrderChannelId;
                state.isInitialized = true;
                if (action.payload.fromCache) {
                    state.error = "Offline — showing cached product data. Connect to sync latest stock & prices.";
                }
            })
            .addCase(loadPOSData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Refresh Products
        builder
            .addCase(refreshProducts.pending, (state) => {
                state.syncing = true;
            })
            .addCase(refreshProducts.fulfilled, (state, action) => {
                state.syncing = false;
                state.products = action.payload.products;
                state.customers = action.payload.customers;
                state.lastSyncAt = action.payload.lastSyncAt;
                state.usingCachedData = false;
                // Clear any cache-warning error
                if (state.error.startsWith("Offline")) state.error = "";
            })
            .addCase(refreshProducts.rejected, (state) => {
                state.syncing = false;
            });

        // Process Order
        builder
            .addCase(processOrder.pending, (state) => {
                state.loading = true;
                state.error = "";
            })
            .addCase(processOrder.fulfilled, (state, action) => {
                state.loading = false;
                // Store the detailed order (with items) so "View Last Receipt" shows full data
                state.lastCompletedOrder = action.payload.detailedOrder;
                state.lastPaymentMethod = action.payload.paymentMethod;
                state.lastAmountPaid = action.payload.amountPaid;
                state.receiptCustomer = action.payload.customer;
                state.lastReceiptCustomer = action.payload.customer;
                state.receiptOrder = action.payload.detailedOrder;
                state.success = `Order ${action.payload.order.order_number} created (Pending)!`;
                state.paymentDialogOpen = false;
                // Receipt first; closing it asks whether to mark it delivered.
                state.receiptModalOpen = true;
                state.deliveryConfirmPending = true;

                // Clear cart after successful order
                state.cart = [];
                state.selectedCustomer = null;
                state.deliveryCharge = 0;
                state.discount = 0;
            })
            .addCase(processOrder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Confirm Mark Delivered
        builder
            .addCase(confirmMarkDelivered.pending, (state) => {
                state.loading = true;
                state.error = "";
            })
            .addCase(confirmMarkDelivered.fulfilled, (state, action) => {
                state.loading = false;
                state.lastCompletedOrder = action.payload;
                state.deliveryConfirmOpen = false;
                state.receiptCustomer = null;
                state.receiptOrder = null;
                state.success = `Order ${action.payload.order_number} marked as delivered`;
                // The receipt was already shown before this question. The
                // caller (POSSystem) checks whether a balance is still due
                // and, if so, opens PaymentUpdateDialog to settle it.
            })
            .addCase(confirmMarkDelivered.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                // Leave deliveryConfirmOpen so the cashier can retry or skip.
            });
    },
});

// Export actions
export const {
    setProductSearch,
    clearMessages,
    setError,
    setSuccess,
    addToCart,
    updateCartQuantity,
    updateCartItemPrice,
    removeFromCart,
    clearCart,
    setSelectedCustomer,
    addCustomer,
    setDeliveryCharge,
    setDiscount,
    openPaymentDialog,
    closePaymentDialog,
    openQuantityDialog,
    closeQuantityDialog,
    setQuantityValue,
    setQuantityDialogIsGift,
    openQuickProductDialog,
    closeQuickProductDialog,
    openReceiptModal,
    closeReceiptModal,
    skipDeliveryConfirm,
    addProduct,
    initializeFromStorage,
    resetPOSState,
} = posSlice.actions;

// Export selectors
export const selectPOSState = (state: { pos: POSState }) => state.pos;
export const selectCart = (state: { pos: POSState }) => state.pos.cart;
export const selectCartTotal = (state: { pos: POSState }) =>
    state.pos.cart.reduce((sum, item) => sum + item.totalPrice, 0);
export const selectProducts = (state: { pos: POSState }) => state.pos.products;
export const selectCustomers = (state: { pos: POSState }) =>
    state.pos.customers;
export const selectSelectedCustomer = (state: { pos: POSState }) =>
    state.pos.selectedCustomer;
export const selectPOSLoading = (state: { pos: POSState }) => state.pos.loading;
export const selectPOSError = (state: { pos: POSState }) => state.pos.error;
export const selectPOSSuccess = (state: { pos: POSState }) => state.pos.success;

// Export reducer
export default posSlice.reducer;
